import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Switch } from './ui/switch';
import { Plus, Edit, Trash2, Save, Settings, Search, Tag } from 'lucide-react';
import { useApi, useApiMutation } from '../hooks/useApi';
import { serviceItemsApi, companiesApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import CategoryManager from './CategoryManager';
import RichTextEditor from './RichTextEditor';

// Auth-Header wie in Calculator.jsx — Token aus local- oder sessionStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CANONICAL_CATEGORY_NAMES = {
  'cat-webdesign': 'Webdesign',
  'cat-extras': 'Website-Extras',
  'cat-seo': 'SEO / GEO',
  'cat-sea': 'SEA / Google Ads',
  'cat-social': 'Social Media',
  'cat-360': '360° Rundgänge',
  'cat-matterport': 'Matterport 3D',
  'cat-foto': 'Fotografie & Video',
  'cat-wartung': 'Wartung & Hosting',
  'cat-allgemein': 'Allgemein'
};


const ServiceManagement = ({ companyId, onServicesChange, onClose }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isDesignElementModalOpen, setIsDesignElementModalOpen] = useState(false);
  const [companyColors, setCompanyColors] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('services'); // 'services', 'design-elements'
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (column) => {
    if (sortKey === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(column);
      setSortDirection('asc');
    }
  };
  const [newDesignElement, setNewDesignElement] = useState({
    name: '',
    description: '',
    type: 'slider', // 'slider', 'checkbox'
    serviceType: 'webdesign', // 'webdesign', 'photography', 'custom'
    minValue: 0,
    maxValue: 20,
    unit: 'Stück',
    price: 0,
    pricingRules: []
  });
  const [newService, setNewService] = useState({
    category: '',
    name: '',
    description: '',
    detailedDescription: '',
    price: 0,
    type: 'einmalig',
    defaultQuantity: 1,
    unit: '',
    notice: ''
  });

  const { toast } = useToast();
  
  console.log('🔍 [ServiceManagement] Rendering with companyId:', companyId);
  
  const { data: services, loading, refetch } = useApi(
    () => {
      console.log('🔍 [ServiceManagement] Fetching services for companyId:', companyId);
      return serviceItemsApi.getAll('');  // ACCOUNT-WEIT: alle Services aller Brands
    },
    [companyId]
  );
  
  console.log('🔍 [ServiceManagement] Services loaded:', services?.length || 0, 'loading:', loading);
  
  const { mutate: createService, loading: creating } = useApiMutation();
  const { mutate: updateService, loading: updating } = useApiMutation();
  const { mutate: deleteService, loading: deleting } = useApiMutation();

  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [allBrands, setAllBrands] = useState([]);

  React.useEffect(() => {
    companiesApi.getAll().then(r => setAllBrands(r.data || [])).catch(() => {});
  }, []);

  // Load company colors
  React.useEffect(() => {
    const fetchCompanyColors = async () => {
      if (!companyId || companyId === 'default_company') return
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''
        const response = await fetch(`${BACKEND_URL}/api/companies/${companyId}`, { headers: getAuthHeaders() })
        if (response.ok) {
          const company = await response.json()
          setCompanyColors(company)
        }
      } catch (error) {
        console.error('Error loading company colors:', error)
      }
    }
    fetchCompanyColors()
  }, [companyId])
  
  // Load categories from API - VERSION 2.0
  React.useEffect(() => {
    console.log('⭐⭐⭐ NEW CODE LOADED - ServiceManagement v2.0 ⭐⭐⭐');
    const fetchCategories = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        console.log('🔍 [v2.0] Fetching categories for companyId:', companyId);
        const response = await fetch(`${BACKEND_URL}/api/categories/`, { headers: getAuthHeaders() });
        console.log('🔍 [v2.0] Categories API response status:', response.status);
        if (response.ok) {
          const cats = await response.json();
          console.log('🔍 [v2.0] Loaded categories:', cats.length, 'categories:', cats);
          setCategories(cats);
          setCategoriesLoaded(true);
        } else {
          console.error('❌ [v2.0] Failed to load categories:', response.status);
        }
      } catch (error) {
        console.error('❌ [v2.0] Error loading categories:', error);
      }
    };
    
    if (companyId) {
      fetchCategories();
    } else {
      console.log('⚠️ [v2.0] No companyId, skipping category fetch');
    }
  }, [companyId]);

  const handleCreateService = async () => {
    try {
      await createService(() => serviceItemsApi.create({
        ...newService,
        companyId: companyId
      }));
      
      toast({
        title: "Service erstellt",
        description: "Der neue Service wurde erfolgreich erstellt.",
      });
      
      setIsCreateModalOpen(false);
      setNewService({
        category: '',
        name: '',
        description: '',
        detailedDescription: '',
        price: 0,
        type: 'einmalig',
        defaultQuantity: 1,
        unit: '',
        notice: ''
      });
      refetch();
      
      // Notify parent component to reload services
      if (onServicesChange) {
        onServicesChange();
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Service konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  const handleEditService = async () => {
    try {
      await updateService(() => serviceItemsApi.update(editingService.id, editingService));
      
      toast({
        title: "Service aktualisiert",
        description: "Der Service wurde erfolgreich aktualisiert.",
      });
      
      setIsEditModalOpen(false);
      setEditingService(null);
      refetch();
      
      // Notify parent component to reload services
      if (onServicesChange) {
        onServicesChange();
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Service konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diesen Service löschen möchten?')) {
      try {
        await deleteService(() => serviceItemsApi.delete(serviceId));
        
        toast({
          title: "Service gelöscht",
          description: "Der Service wurde erfolgreich gelöscht.",
        });
        
        refetch();
        
        // Notify parent component to reload services
        if (onServicesChange) {
          onServicesChange();
        }
      } catch (error) {
        toast({
          title: "Fehler",
          description: "Service konnte nicht gelöscht werden.",
          variant: "destructive",
        });
      }
    }
  };

  // Filter services based on search term and category
  const filteredServices = services?.filter(service => {
    const matchesSearch = !searchTerm || 
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Fix: Compare category - service.category can be either ID or name
    const matchesCategory = selectedCategory === 'all' || (() => {
      const selectedCat = categories.find(c => c.id === selectedCategory || c.value === selectedCategory);
      if (!selectedCat) return false;
      
      // Match by ID (new format) or by name (legacy format)
      const catId = selectedCat.id || selectedCat.value;
      const catName = selectedCat.name || selectedCat.label;
      
      return service.category === catId || service.category === catName;
    })();
    
    return matchesSearch && matchesCategory;
  }) || [];

  const openEditModal = (service) => {
    setEditingService({ ...service });
    setIsEditModalOpen(true);
  };

  if (loading) {
    return <div className="flex justify-center py-8">Lade Services...</div>;
  }

  const customServices = services?.filter(s => s.isCustom) || [];
  const defaultServices = services?.filter(s => !s.isCustom) || [];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-800">Service-Verwaltung</h2>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => setIsCategoryManagerOpen(true)}
          >
            <Tag className="w-4 h-4" />
            Kategorien verwalten
          </Button>

          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Neuer Service
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Neuen Service erstellen</DialogTitle>
              </DialogHeader>
              <ServiceForm
                service={newService}
                onChange={setNewService}
                onSave={handleCreateService}
                categories={categories}
                loading={creating}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Services durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Kategorie wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id || category.value} value={category.id || category.value}>
                    {category.icon && `${category.icon} `}{category.name || category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {(searchTerm || selectedCategory !== 'all') && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <span>
              {filteredServices.length} von {services?.length || 0} Services
            </span>
            {searchTerm && (
              <Badge variant="outline" onClick={() => setSearchTerm('')} className="cursor-pointer">
                Suche: "{searchTerm}" ✕
              </Badge>
            )}
            {selectedCategory !== 'all' && (
              <Badge variant="outline" onClick={() => setSelectedCategory('all')} className="cursor-pointer">
                {(() => {
                  const cat = categories.find(c => c.id === selectedCategory || c.value === selectedCategory);
                  return cat ? `${cat.icon || ''} ${cat.name || cat.label}` : selectedCategory;
                })()} ✕
              </Badge>
            )}
          </div>
        )}
      </Card>

      {/* Services */}
      {filteredServices.length > 0 ? (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Services
            </h3>
            <Badge variant="outline">
              {filteredServices.length} Service{filteredServices.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <ServiceTable
            allBrands={allBrands}
            services={filteredServices}
            categories={categories}
            onEdit={openEditModal}
            onDelete={handleDeleteService}
            canEdit={true}
            companyColors={companyColors}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        </Card>
      ) : (
        <Card className="p-6">
          <div className="text-center py-8">
            <Settings className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              {(searchTerm || selectedCategory !== 'all') 
                ? 'Keine Services gefunden' 
                : 'Keine Services verfügbar'
              }
            </h3>
            <p className="text-slate-500 mb-4">
              {(searchTerm || selectedCategory !== 'all')
                ? 'Versuchen Sie eine andere Suche oder Kategorie.'
                : 'Erstellen Sie Ihren ersten Service über den "Neuer Service" Button.'
              }
            </p>
            {(searchTerm || selectedCategory !== 'all') && (
              <Button variant="outline" onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}>
                Alle Services anzeigen
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Service bearbeiten</DialogTitle>
          </DialogHeader>
          {editingService && (
            <ServiceForm
              service={editingService}
              onChange={setEditingService}
              onSave={handleEditService}
              categories={categories}
              loading={updating}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Category Manager Dialog */}
      <CategoryManager
        companyId={companyId}
        open={isCategoryManagerOpen}
        onOpenChange={setIsCategoryManagerOpen}
        onCategoriesChange={async () => {
          // Reload categories and services
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
          const response = await fetch(`${BACKEND_URL}/api/categories/`, { headers: getAuthHeaders() });
          if (response.ok) {
            const cats = await response.json();
            setCategories(cats);
          }
          refetch();
          if (onServicesChange) onServicesChange();
        }}
      />
    </div>
  );
};

const ServiceForm = ({ service, onChange, onSave, categories, loading }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Kategorie</Label>
        <Select value={service.category} onValueChange={(value) => onChange({ ...service, category: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map(cat => (
              <SelectItem key={cat.id || cat.value} value={cat.id || cat.value}>
                {cat.icon && `${cat.icon} `}{cat.name || cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Service-Name</Label>
        <Input
          value={service.name}
          onChange={(e) => onChange({ ...service, name: e.target.value })}
          placeholder="z.B. WordPress Wartung"
        />
      </div>
    </div>

    <div>
      <Label>Kurzbeschreibung</Label>
      <Textarea
        value={service.description}
        onChange={(e) => onChange({ ...service, description: e.target.value })}
        placeholder="Kurze Beschreibung des Services"
        rows={2}
      />
    </div>

    <div>
      <Label>Detaillierte Beschreibung</Label>
      <RichTextEditor
        value={service.detailedDescription || ''}
        onChange={(content) => onChange({ ...service, detailedDescription: content })}
        placeholder="Ausführliche Beschreibung mit allen Leistungen..."
        height={200}
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Preis (€)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={service.price}
            onChange={(e) => onChange({ ...service, price: parseFloat(e.target.value) || 0 })}
          />
        </div>
        
        <div>
          <Label>Abrechnungsart</Label>
          <Select value={service.type} onValueChange={(value) => onChange({ ...service, type: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="einmalig">Einmalig</SelectItem>
              <SelectItem value="monatlich">Monatlich</SelectItem>
              <SelectItem value="jährlich">Jährlich</SelectItem>
              <SelectItem value="variabel">Variabel (Pay-as-you-go)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Standard-Menge</Label>
          <Input
            type="number"
            min="1"
            value={service.defaultQuantity || 1}
            onChange={(e) => onChange({ ...service, defaultQuantity: parseInt(e.target.value) || 1 })}
            placeholder="1"
          />
          <p className="text-xs text-slate-500 mt-1">Wird in Angeboten vorausgefüllt</p>
        </div>
        
        <div>
          <Label>Einheit (optional)</Label>
          <Input
            list="unit-suggestions"
            value={service.unit || ''}
            onChange={(e) => onChange({ ...service, unit: e.target.value })}
            placeholder="z.B. Stunden, Projekt, Lizenz"
          />
          <datalist id="unit-suggestions">
            <option value="Stunden" />
            <option value="Tage" />
            <option value="Seiten" />
            <option value="Stück" />
            <option value="Projekt" />
            <option value="Lizenz" />
            <option value="Monat" />
            <option value="Jahr" />
            <option value="Benutzer" />
            <option value="Räume" />
            <option value="Standorte" />
            <option value="Pauschal" />
          </datalist>
          <p className="text-xs text-slate-500 mt-1">Vorschläge auswählen oder eigenen Wert eingeben</p>
        </div>
      </div>
    </div>

    <div>
      <Label>Hinweis (optional)</Label>
      <Input
        value={service.notice || ''}
        onChange={(e) => onChange({ ...service, notice: e.target.value })}
        placeholder="z.B. Zusätzliche externe Kosten..."
      />
    </div>

    {/* Staffelpreise */}
    <div className="border rounded-lg p-4 bg-slate-50 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Staffelpreise aktivieren</Label>
          <p className="text-xs text-slate-500 mt-0.5">Verschiedene Preise je nach Menge (z.B. 1-5 Stk: 300€, ab 6: 250€)</p>
        </div>
        <Switch
          checked={!!(service.priceTiers && service.priceTiers.length > 0)}
          onCheckedChange={(checked) => {
            if (checked) {
              onChange({ ...service, priceTiers: [
                { upTo: 5, pricePerUnit: service.price || 0 },
                { upTo: null, pricePerUnit: Math.round((service.price || 0) * 0.9) }
              ]});
            } else {
              onChange({ ...service, priceTiers: null });
            }
          }}
        />
      </div>

      {service.priceTiers && service.priceTiers.length > 0 && (
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-600 px-1">
            <span className="col-span-5">Bis Menge</span>
            <span className="col-span-5">Preis pro Einheit (€)</span>
            <span className="col-span-2"></span>
          </div>
          {service.priceTiers.map((tier, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-5">
                {tier.upTo === null ? (
                  <Input value="unbegrenzt" disabled className="bg-slate-100 text-slate-500 text-sm" />
                ) : (
                  <Input
                    type="number"
                    min="1"
                    value={tier.upTo}
                    onChange={(e) => {
                      const updated = [...service.priceTiers];
                      updated[i] = { ...updated[i], upTo: parseInt(e.target.value) || 1 };
                      onChange({ ...service, priceTiers: updated });
                    }}
                    className="text-sm"
                  />
                )}
              </div>
              <div className="col-span-5">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tier.pricePerUnit}
                  onChange={(e) => {
                    const updated = [...service.priceTiers];
                    updated[i] = { ...updated[i], pricePerUnit: parseFloat(e.target.value) || 0 };
                    onChange({ ...service, priceTiers: updated });
                  }}
                  className="text-sm"
                />
              </div>
              <div className="col-span-2 flex justify-end">
                {service.priceTiers.length > 1 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const updated = service.priceTiers.filter((_, idx) => idx !== i);
                      // Ensure last tier always has upTo: null
                      updated[updated.length - 1] = { ...updated[updated.length - 1], upTo: null };
                      onChange({ ...service, priceTiers: updated });
                    }}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const tiers = [...service.priceTiers];
              // Set current last tier to have a specific upTo, add new unlimited tier
              const lastTier = tiers[tiers.length - 1];
              const prevUpTo = tiers.length >= 2 ? tiers[tiers.length - 2].upTo || 10 : 5;
              tiers[tiers.length - 1] = { ...lastTier, upTo: prevUpTo + 10 };
              tiers.push({ upTo: null, pricePerUnit: lastTier.pricePerUnit });
              onChange({ ...service, priceTiers: tiers });
            }}
            className="text-xs"
          >
            <Plus className="h-3 w-3 mr-1" /> Staffel hinzufügen
          </Button>
          <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
            Die letzte Zeile gilt für alle Mengen darüber hinaus. Preis gilt für die Gesamtmenge (Pauschalpreis pro Stufe).
          </p>
        </div>
      )}
    </div>

    <div className="flex justify-end gap-2 pt-4">
      <Button onClick={onSave} disabled={loading} className="flex items-center gap-2">
        <Save className="w-4 h-4" />
        {loading ? 'Speichere...' : 'Speichern'}
      </Button>
    </div>
  </div>
);

const ServiceTable = ({ services, categories, onEdit, onDelete, canEdit, companyColors, sortKey, sortDirection, onSort, allBrands = [] }) => {
  // Sort services
  const sortedServices = [...services].sort((a, b) => {
    let aVal, bVal;
    switch (sortKey) {
      case 'name':
        aVal = a.name?.toLowerCase() || '';
        bVal = b.name?.toLowerCase() || '';
        break;
      case 'category':
        aVal = a.category?.toLowerCase() || '';
        bVal = b.category?.toLowerCase() || '';
        break;
      case 'price':
        aVal = a.price || 0;
        bVal = b.price || 0;
        break;
      case 'type':
        aVal = a.type?.toLowerCase() || '';
        bVal = b.type?.toLowerCase() || '';
        break;
      default:
        return 0;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortHeader = (column, label) => (
    <TableHead 
      className="cursor-pointer hover:bg-slate-50 select-none"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === column && (
          <span className="text-blue-600">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </TableHead>
  );

  return (
  <Table>
    <TableHeader>
      <TableRow>
        {renderSortHeader('name', 'Service')}
        {renderSortHeader('category', 'Kategorie')}
        {renderSortHeader('price', 'Preis')}
        {renderSortHeader('type', 'Typ')}
        <TableHead className="text-right">Aktionen</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {sortedServices.map((service) => (
        <TableRow key={service.id}>
          <TableCell>
            <div>
              <p className="font-medium">{service.name}</p>
              <p className="text-sm text-gray-500">{service.description}</p>
              {/* Sichtbarkeit: bei welchen Brands ist der Service im Katalog? */}
              <div className="flex flex-wrap gap-1 mt-1">
                {(service.brandIds?.length
                  ? service.brandIds
                  : (service.companyId ? [service.companyId] : [])
                ).map((bid) => {
                  const b = allBrands.find(c => c.id === bid);
                  return b ? (
                    <span key={bid} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                      {b.name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          </TableCell>
          <TableCell>
            <Badge variant="outline">
              {(() => {
                console.log('🔍 Finding category for service:', service.name, 'category:', service.category, 'available categories:', categories.length);
                const cat = categories.find(c => c.id === service.category || c.value === service.category);
                console.log('🔍 Found category:', cat);
                return cat ? `${cat.icon || ''} ${cat.name || cat.label}` : (CANONICAL_CATEGORY_NAMES[service.category] || service.category);
              })()}
            </Badge>
          </TableCell>
          <TableCell>
            <span className="font-medium">{service.price.toLocaleString('de-DE')}€</span>
          </TableCell>
          <TableCell>
            <Badge 
              className={
                service.type === 'einmalig' 
                  ? 'bg-green-100 text-green-800' 
                  : service.type === 'monatlich' 
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-purple-100 text-purple-800'
              }
              style={
                service.type === 'jährlich' && companyColors?.jaehrlichColor
                  ? { 
                      backgroundColor: companyColors.jaehrlichColor + '20',
                      color: companyColors.jaehrlichColor,
                      borderColor: companyColors.jaehrlichColor + '40'
                    }
                  : undefined
              }
            >
              {service.type}
            </Badge>
          </TableCell>
          <TableCell className="text-right">
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => onEdit(service)}>
                <Edit className="w-3 h-3" />
              </Button>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => onDelete(service.id)} className="text-red-600">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
  );
};

export default ServiceManagement;